import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type { NoticeDTO } from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { fmtDate } from "../lib/format";
import { RichTextRender } from "../components/RichTextRender";
import { StatusBadge, Loading, ErrorBanner, Banner } from "../components/ui";

export function NoticeDetailPage() {
  const { noticeId } = useParams<{ noticeId: string }>();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [data, setData] = useState<NoticeDTO | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!noticeId) return;
    api
      .get<NoticeDTO>(`/notices/${noticeId}${preview ? "?preview=1" : ""}`)
      .then((d) => {
        setData(d);
        // プレビュー時は既読を立てない (テスト確認のため)
        if (!preview)
          api.post(`/notices/${noticeId}/read`).catch(() => {});
      })
      .catch((e) => setErr((e as Error).message));
  }, [noticeId, preview]);

  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!data) return <Loading />;

  const pinned =
    data.pinnedUntil && new Date(data.pinnedUntil).getTime() > Date.now();
  const thumb = uploadsUrl(data.thumbnailKey);

  return (
    <article className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/notices"
        className="inline-block text-sm text-tora-ink/60 hover:text-tora"
      >
        ← お知らせ一覧へ
      </Link>

      {preview && (
        <Banner tone="warning">
          プレビュー表示中です（未公開／予約中の可能性があります）。会員にはまだ表示されていません。
        </Banner>
      )}

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[11px] text-tora-accent tracking-widest font-medium">
            NOTICE
          </p>
          {pinned && (
            <StatusBadge tone="warning" bold>
              📌 ピン留め
            </StatusBadge>
          )}
          {data.publishedAt && (
            <span className="text-xs text-tora-ink/60">
              {fmtDate(data.publishedAt)}
            </span>
          )}
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-tora leading-tight">
          {data.title}
        </h2>
      </header>

      {thumb && (
        <img
          src={thumb}
          alt=""
          className="w-full h-auto max-h-[70vh] object-contain rounded-xl border border-black/10 bg-white shadow-sm"
        />
      )}

      <RichTextRender html={data.body} />
    </article>
  );
}
